<template>

    <kt-modal :visible="recordVisible" :title="recordTitle" @dismiss="onDismiss()" size="modal-lg">
        <template #body>
            <expense-form v-model="record"></expense-form>
        </template>

        <template #footer>
            <div class="row w-100">
                <div :class="{ 'col-4': record.Id, 'col-6': !record.Id }">
                    <button @click="onDismiss()" class="btn btn-sm btn-outline-secondary form-control">Close</button>
                </div>
                <div class="col-4" v-if="record.Id">
                    <button @click="deleteRecord()" class="btn btn-sm btn-outline-danger form-control">Delete</button>
                </div>
                <div :class="{ 'col-4': record.Id, 'col-6': !record.Id }">
                    <button @click="saveRecord()" class="btn btn-sm btn-outline-primary form-control">Save</button>
                </div>
            </div>
        </template>
    </kt-modal>

</template>

<script lang="ts" setup>

import { ref, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

// import Api from "@/services/Api.js";

import ApiService from "@/services/ApiService";
import Emitter from "@/services/Emitter";

import Helper from "@/services/Helper";
import Alert from "@/services/Alert";
import ExpenseForm from "@/components/Expenses/ExpenseForm.vue";
import KtModal from "@/components/UI/KtModal.vue";

import { ExpenseModel } from "@/models/ExpenseModel";

const route = useRoute();
const router = useRouter();
const recordTitle = ref("Expense Entry");
const recordVisible = ref(true);

const props = defineProps({
    id: {
        type: [Number, String],
        default: null
    },
    requestId: {
        type: Number,
        default: 0
    },
    clientId: {
        type: Number,
        default: 0
    },
    techId: {
        type: Number,
        default: 0
    },
    embedded: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(["cancel", "saved"]);

const newRecord = (): ExpenseModel => {
    const expense = new ExpenseModel();
    // Pre-populate with context if provided
    if (props.requestId) {
        expense.RequestId = props.requestId;
    }
    if (props.clientId) {
        expense.CustomerNumber = props.clientId;
    }
    if (props.techId) {
        expense.AssignedTo = props.techId;
    }
    // Set default expense date to today
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    expense.ExpenseDate = `${year}-${month}-${day}`;
    return expense;
};

const record = ref<ExpenseModel>(newRecord());

const saveRecord = async () => 
{
    // prep the record ...
    let data = Helper.prep(record.value);
    
    // TODO - validate record here
    console.debug("save", data);
    
    // save ...
    data = await ApiService.saveExpense(data);

    // reload any stores you need to ...
    // ...

    if (props.embedded) {
        emit("saved");
    } else {
        // redirect ...
        Emitter.emit("redirect", "/expense/edit/" + data.Id);
    }
};

const deleteRecord = async () => 
{
    Alert.notify("Deletion has been disabled");
};


const onDismiss = () => 
{
    if (props.embedded) {
        emit("cancel");
    } else {
        router.back();
    }
};

const unwatch:Array <Function> = [];

const loadRecord = async (val?: string | number | null) =>
{
    // Priority: id prop > embedded mode > route params
    if (props.id !== null && props.id !== undefined) {
        // Global modal mode with id prop
        if (props.id && props.id !== "new") {
            let rec = await ApiService.loadExpense(props.id);
            console.debug("expense", rec);
            record.value = rec;
        } else {
            record.value = newRecord();
        }
    } else if (props.embedded && props.requestId) {
        // In embedded mode, create a new expense for the request
        let newRec = newRecord();
        newRec.RequestId = props.requestId;
        record.value = newRec;
    } else if (val) {
        // Route-based loading
        if (val === "new") {
            record.value = newRecord();
        } else {
            let rec = await ApiService.loadExpense(val);
            console.debug("expense", rec);
            record.value = rec;
        }
    } else {
        record.value = newRecord();
    }
};

// Watch id prop for global modal usage
unwatch.push(watch(() => props.id, async (newId) => {
    if (newId !== null && newId !== undefined) {
        await loadRecord();
    }
}));

if (!props.embedded && props.id === null) {
    // Only watch route params if not in embedded mode and no id prop
    unwatch.push(watch(() => route.params.id, async (nv, ov) => 
    {
        // Handle both new and edit routes
        let idValue = nv;
        if (Array.isArray(idValue)) {
            idValue = idValue[0];
        }
        if (!idValue && route.name === "Expense New") {
            loadRecord("new");
        } else if (idValue) {
            loadRecord(idValue);
        }
    }, 
    { 
        immediate: true 
    }));
} else if (props.id !== null) {
    // Load immediately when id prop is provided
    loadRecord();
} else if (props.embedded) {
    // Load immediately in embedded mode
    loadRecord("new");
}

onUnmounted(() => 
{
    unwatch.forEach((un) => 
    {
        un();
    });
});

</script>